import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import {
  Activity,
  Company,
  Deal,
  EntityType,
  EntityTypeSupportingPreview,
  Person,
} from "@mapmycustomers/shared/types/entity";
import { PREVIEW_ENTITY_ATTR } from "@mapmycustomers/shared/util/browser";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { LoadingSpinner } from "@mapmycustomers/ui";

import InterceptNavigationMessageModal from "@app/component/InterceptNavigationModal/components/InterceptNavigationMessageModal";
import {
  ActivityRecordPane,
  CompanyRecordPane,
  DealRecordPane,
  PersonRecordPane,
} from "@app/component/preview";
import {
  getEntityTypesWithExternalPreview,
  getEntityViewElement,
  getEntityViewEntityId,
  getEntityViewEntityType,
  getEntityViewParentEntity,
  getPreviewPaneZIndex,
  shouldEditPreviewedEntity,
} from "@app/store/entityView";
import { hideEntityView, showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import eventEmitter, { Events } from "@app/util/eventEmitter";

import ActivityAnnotation from "../../activity/ActivityAnnotation";

const entityTypeToSaveChangesEvent: Record<EntityTypeSupportingPreview, keyof Events> = {
  [EntityType.ACTIVITY]: "saveActivityChanges",
  [EntityType.COMPANY]: "saveCompanyChanges",
  [EntityType.DEAL]: "saveDealChanges",
  [EntityType.PERSON]: "savePersonChanges",
};

interface PreviewEditPanesProps {
  entityTypesWithExternalPreview: EntityType[];
}

const PreviewEditPanes = ({
  edit,
  elementId,
  entityId,
  entityType,
  entityTypesWithExternalPreview,
  onHide,
  onShowEntityView,
  parentEntity,
  zIndex,
}: PreviewEditPanesProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const [preview, setPreview] = useState({ entityId, entityType });
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmExitModalVisible, setConfirmExitModalVisible] = useState(false);

  const element = useMemo(() => {
    if (!elementId) {
      return undefined;
    }
    return (
      (document.querySelector(`[${PREVIEW_ENTITY_ATTR}="${elementId}"]`) as HTMLElement) ??
      undefined
    );
  }, [elementId]);

  const handleClose = useCallback(() => {
    onHide();
    if (element) {
      element.removeAttribute(PREVIEW_ENTITY_ATTR);
    }
  }, [element, onHide]);

  const handleHide = useDynamicCallback(() => {
    if (hasChanges) {
      setConfirmExitModalVisible(true);
    } else {
      handleClose();
    }
  });

  const handleLeave = useCallback(() => {
    setConfirmExitModalVisible(false);
    setHasChanges(false);
    if (entityId !== preview.entityId) {
      setPreview({ entityId, entityType });
    } else if (parentEntity) {
      onShowEntityView({
        entityId: parentEntity.entityId,
        entityType: parentEntity.entityType,
        returnState: parentEntity.returnState,
      });
    } else {
      handleClose();
    }
  }, [entityId, entityType, handleClose, onShowEntityView, parentEntity, preview.entityId]);

  const handleStay = useCallback(() => {
    setConfirmExitModalVisible(false);
    if (entityId !== preview.entityId) {
      onShowEntityView({ entityId: preview.entityId, entityType: preview.entityType });
    }
  }, [entityId, onShowEntityView, preview]);

  const handleSave = useCallback(() => {
    if (preview.entityType) {
      eventEmitter.emit(
        entityTypeToSaveChangesEvent[preview.entityType as EntityTypeSupportingPreview]
      );
      handleLeave();
    }
  }, [preview.entityType, handleLeave]);

  useEffect(() => {
    if (hasChanges && preview.entityId && entityId && entityId !== preview.entityId) {
      setConfirmExitModalVisible(true);
    } else {
      setPreview({ entityId, entityType });
    }
  }, [hasChanges, entityId, preview.entityId, entityType]);

  const handleBack = useCallback(() => {
    if (hasChanges) {
      setConfirmExitModalVisible(true);
    } else {
      if (parentEntity) {
        onHide();
        onShowEntityView({
          entityId: parentEntity.entityId,
          entityType: parentEntity.entityType,
          returnState: parentEntity.returnState,
        });
      }
    }
  }, [hasChanges, onHide, onShowEntityView, parentEntity]);

  if (preview.entityType && entityTypesWithExternalPreview.includes(preview.entityType)) {
    return null;
  }

  return (
    <>
      {preview.entityType === EntityType.ACTIVITY && !edit && (
        <React.Suspense fallback={<LoadingSpinner global />}>
          <ActivityAnnotation
            activityId={preview.entityId as Activity["id"]}
            eventElement={element}
            onEdit={onShowEntityView}
            onHide={handleHide}
          />
        </React.Suspense>
      )}
      {preview.entityType === EntityType.ACTIVITY && edit && (
        <React.Suspense fallback={<LoadingSpinner global />}>
          <ActivityRecordPane
            activityId={preview.entityId as Activity["id"]}
            onBack={parentEntity ? handleBack : undefined}
            onChange={setHasChanges}
            onHide={handleHide}
            parentEntityName={parentEntity?.entityName}
            zIndex={zIndex}
          />
        </React.Suspense>
      )}
      {preview.entityType === EntityType.COMPANY && (
        <React.Suspense fallback={<LoadingSpinner global />}>
          <CompanyRecordPane
            className="layout"
            companyId={preview.entityId as Company["id"]}
            onChange={setHasChanges}
            onHide={handleHide}
            zIndex={zIndex}
          />
        </React.Suspense>
      )}
      {preview.entityType === EntityType.DEAL && (
        <React.Suspense fallback={<LoadingSpinner global />}>
          <DealRecordPane
            dealId={preview.entityId as Deal["id"]}
            onChange={setHasChanges}
            onHide={handleHide}
            zIndex={zIndex}
          />
        </React.Suspense>
      )}
      {preview.entityType === EntityType.PERSON && (
        <React.Suspense fallback={<LoadingSpinner global />}>
          <PersonRecordPane
            onChange={setHasChanges}
            onHide={handleHide}
            personId={preview.entityId as Person["id"]}
            zIndex={zIndex}
          />
        </React.Suspense>
      )}
      <InterceptNavigationMessageModal
        onLeave={handleLeave}
        onSave={handleSave}
        onStay={handleStay}
        visible={confirmExitModalVisible}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  edit: shouldEditPreviewedEntity(state),
  elementId: getEntityViewElement(state),
  entityId: getEntityViewEntityId(state),
  entityType: getEntityViewEntityType(state),
  entityTypesWithExternalPreview: getEntityTypesWithExternalPreview(state),
  parentEntity: getEntityViewParentEntity(state),
  zIndex: getPreviewPaneZIndex(state),
});

const mapDispatchToProps = {
  onHide: hideEntityView,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(PreviewEditPanes);
